/**
 * Especializacao de `<MultiSelect>` para selecao assincrona de usuarios.
 *
 * Consome `GET /api/v1/admin/users/search` (debounce 300ms, query >= 3 chars)
 * via TanStack Query e renderiza chips com avatar/nome + opcoes com avatar +
 * nome + email + role badge.
 *
 * Atende RF45 (TASK-021).
 *
 * Decisao de design: `value` permanece como `string[]` (userIds) — alinhado
 * com a API legada e o backend. Mantemos um cache local
 * `selectedUsersCache` que cresce conforme o usuario seleciona itens da
 * busca, fornecendo rich data para os chips. IDs externos sem cache
 * exibem o proprio ID como label ate aparecerem em uma busca subsequente.
 *
 * O endpoint `/api/v1/admin/users/search` ainda nao esta no
 * `api.d.ts` gerado, entao usamos `fetch` com auth header — alinhado ao
 * padrao de `client.ts` mas sem typed paths.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { Badge } from '@favelafood/shared/components/ui';
import { cn } from '@favelafood/shared/lib/cn';
import { ApiError } from '@favelafood/shared/api/errors';
import { getAccessToken } from '@favelafood/shared/auth/tokens';
import { MultiSelect } from './MultiSelect';

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LEN = 3;
const STALE_TIME_MS = 30_000;
const SEARCH_PAGE_SIZE = 20;

const ROLE_LABEL: Record<UserRole, string> = {
  CUSTOMER: 'Cliente',
  MERCHANT: 'Lojista',
  COURIER: 'Entregador',
  ADMIN: 'Admin',
};

type UserRole = 'CUSTOMER' | 'MERCHANT' | 'COURIER' | 'ADMIN';

interface UserOption {
  id: string;
  name: string;
  email: string;
  role: UserRole | null;
  avatarUrl?: string;
}

export interface UserMultiSelectProps {
  /** Array controlado de userIds. */
  value: ReadonlyArray<string>;
  /** Callback ao alterar a selecao. */
  onChange: (userIds: string[]) => void;
  /** Filtro opcional por roles do backend. */
  filterRoles?: ReadonlyArray<UserRole>;
  placeholder?: string;
  disabled?: boolean;
}

interface RawUserHit {
  id?: string;
  name?: string;
  email?: string;
  role?: string;
  avatarUrl?: string;
}

const apiBase: string = import.meta.env.VITE_API_BASE ?? '';

/** Hook simples de debounce — evita disparar fetch a cada tecla. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Normaliza role string vindo do backend para o union type. */
function toUserRole(value: string | undefined): UserRole | null {
  if (value === 'CUSTOMER' || value === 'MERCHANT' || value === 'COURIER' || value === 'ADMIN') {
    return value;
  }
  return null;
}

/** Mapeia hit do backend para `UserOption`. */
function mapHit(hit: RawUserHit): UserOption | null {
  if (!hit.id) return null;
  const option: UserOption = {
    id: hit.id,
    name: hit.name ?? hit.id,
    email: hit.email ?? '',
    role: toUserRole(hit.role),
  };
  if (hit.avatarUrl !== undefined) {
    return { ...option, avatarUrl: hit.avatarUrl };
  }
  return option;
}

/** Constroi querystring respeitando filtros opcionais. */
function buildSearchUrl(query: string, roles: ReadonlyArray<UserRole>): string {
  const params = new URLSearchParams();
  params.set('size', String(SEARCH_PAGE_SIZE));
  if (query.length > 0) params.set('q', query);
  // O endpoint atual aceita apenas um `role` por chamada — usamos o primeiro.
  // Multi-role exige multiplas chamadas (fora de escopo desta task).
  const firstRole = roles[0];
  if (firstRole !== undefined) params.set('role', firstRole);
  return `${apiBase}/api/v1/admin/users/search?${params.toString()}`;
}

/** Fetch helper com auth header (espelha o middleware de `client.ts`). */
async function fetchUsers(
  query: string,
  roles: ReadonlyArray<UserRole>,
  signal: AbortSignal,
): Promise<UserOption[]> {
  const token = getAccessToken();
  const headers: HeadersInit = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(buildSearchUrl(query, roles), { headers, signal });
  if (!res.ok) {
    throw new ApiError(res.status, { message: res.statusText }, `HTTP ${res.status}`);
  }
  const json = (await res.json()) as ReadonlyArray<RawUserHit>;
  return json.map(mapHit).filter((opt): opt is UserOption => opt !== null);
}

/** Iniciais para o avatar fallback. */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

interface AvatarProps {
  src: string | undefined;
  name: string;
  size?: number;
}

function Avatar({ src, name, size = 28 }: AvatarProps): ReactElement {
  const dim = `${size}px`;
  if (src && src.length > 0) {
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        width={size}
        height={size}
        style={{ width: dim, height: dim }}
        className="rounded-full object-cover"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      style={{ width: dim, height: dim }}
      className={cn(
        'inline-flex items-center justify-center rounded-full ' +
          'bg-bg-hover text-xs font-medium text-white',
      )}
    >
      {getInitials(name)}
    </span>
  );
}

/** Render do chip de usuario selecionado. */
function renderUserChip(option: UserOption, onRemove: () => void): ReactElement {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
    onRemove();
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-border bg-bg-hover ' +
          'pl-1 pr-1.5 py-0.5 text-xs text-white',
      )}
    >
      <Avatar src={option.avatarUrl} name={option.name} size={20} />
      <span className="max-w-[10rem] truncate">{option.name}</span>
      {option.role ? (
        <span className="text-[10px] uppercase tracking-wide text-muted">
          {ROLE_LABEL[option.role]}
        </span>
      ) : null}
      <button
        type="button"
        onClick={handleClick}
        aria-label={`Remover ${option.name}`}
        className={cn(
          'flex h-4 w-4 items-center justify-center rounded-full text-muted ' +
            'transition-colors hover:bg-danger/20 hover:text-danger ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger',
        )}
      >
        <X size={12} aria-hidden />
      </button>
    </span>
  );
}

/** Render de cada opcao no dropdown. */
function renderUserOption(option: UserOption): ReactElement {
  return (
    <span className="flex w-full items-center gap-2">
      <Avatar src={option.avatarUrl} name={option.name} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm text-white">{option.name}</span>
        {option.email ? (
          <span className="truncate text-xs text-muted">{option.email}</span>
        ) : null}
      </span>
      {option.role ? (
        <Badge variant="muted" className="shrink-0">
          {ROLE_LABEL[option.role]}
        </Badge>
      ) : null}
    </span>
  );
}

/**
 * Combina opcoes ja selecionadas (que talvez nao apareçam na busca atual)
 * com os resultados frescos. Garante que o usuario sempre veja seus chips
 * marcados na lista quando disponivel.
 */
function mergeOptions(
  searchResults: ReadonlyArray<UserOption>,
  selected: ReadonlyArray<UserOption>,
): UserOption[] {
  const seen = new Set<string>();
  const merged: UserOption[] = [];
  for (const opt of searchResults) {
    if (!seen.has(opt.id)) {
      merged.push(opt);
      seen.add(opt.id);
    }
  }
  for (const opt of selected) {
    if (!seen.has(opt.id)) {
      merged.push(opt);
      seen.add(opt.id);
    }
  }
  return merged;
}

/** Cria UserOption placeholder para IDs sem cache. */
function placeholderUser(id: string): UserOption {
  return { id, name: id, email: '', role: null };
}

export function UserMultiSelect(props: UserMultiSelectProps): ReactElement {
  const { value, onChange, filterRoles, placeholder = 'Buscar usuários...', disabled } = props;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounced(searchTerm, DEBOUNCE_MS);
  const cacheRef = useRef<Map<string, UserOption>>(new Map());

  const roles = useMemo<ReadonlyArray<UserRole>>(() => filterRoles ?? [], [filterRoles]);
  const enabled = debouncedSearch.length >= MIN_SEARCH_LEN;

  const queryResult = useQuery({
    queryKey: ['admin-users-search', debouncedSearch, roles] as const,
    queryFn: ({ signal }) => fetchUsers(debouncedSearch, roles, signal),
    enabled,
    staleTime: STALE_TIME_MS,
  });

  // Atualiza cache local com hits frescos
  useEffect(() => {
    const data = queryResult.data;
    if (!data) return;
    const cache = cacheRef.current;
    for (const opt of data) cache.set(opt.id, opt);
  }, [queryResult.data]);

  // Reconstroi `selected` a partir do cache + placeholders
  const selected = useMemo<UserOption[]>(() => {
    const cache = cacheRef.current;
    return value.map((id) => cache.get(id) ?? placeholderUser(id));
  }, [value]);

  // Garante que selected entries tambem entrem no cache (idempotente)
  useEffect(() => {
    const cache = cacheRef.current;
    for (const opt of selected) {
      if (!cache.has(opt.id)) cache.set(opt.id, opt);
    }
  }, [selected]);

  const dropdownOptions = useMemo(
    () => mergeOptions(queryResult.data ?? [], selected),
    [queryResult.data, selected],
  );

  const handleChange = useCallback(
    (next: UserOption[]): void => {
      onChange(next.map((u) => u.id));
    },
    [onChange],
  );

  // Bypass do filtro local — o backend ja faz o filtering via /search.
  // Mantemos um filtro permissivo para nao esconder nada quando o user
  // digita rapido (a busca local fica em sync com o servidor).
  const passThroughFilter = useCallback((): boolean => true, []);

  const emptyMessage = computeEmptyMessage(
    debouncedSearch,
    queryResult.isFetching,
    queryResult.isError,
  );

  return (
    <div className="relative">
      <MultiSelect<UserOption>
        options={dropdownOptions}
        value={selected}
        onChange={handleChange}
        getOptionKey={(o) => o.id}
        getOptionLabel={(o) => o.name}
        filterFn={passThroughFilter}
        renderOption={renderUserOption}
        renderChip={renderUserChip}
        placeholder={placeholder}
        disabled={disabled ?? false}
        emptyMessage={emptyMessage}
        ariaLabel="Selecionar usuários"
        onSearchChange={setSearchTerm}
      />
      {queryResult.isFetching && enabled ? (
        <span
          className="pointer-events-none absolute right-9 top-3 text-muted"
          aria-hidden="true"
        >
          <Loader2 size={14} className="animate-spin" />
        </span>
      ) : null}
      {!enabled && searchTerm.length > 0 ? (
        <span className="sr-only" role="status">
          Digite ao menos {MIN_SEARCH_LEN} caracteres para buscar
        </span>
      ) : null}
    </div>
  );
}

/**
 * Mensagem dinamica para o estado vazio do dropdown.
 * Mantida fora do componente para baixar a CC do render.
 */
function computeEmptyMessage(query: string, isFetching: boolean, isError: boolean): string {
  if (isError) return 'Falha ao buscar usuários';
  if (query.length < MIN_SEARCH_LEN) {
    return `Digite ao menos ${MIN_SEARCH_LEN} caracteres`;
  }
  if (isFetching) return 'Buscando...';
  return 'Nenhum usuário encontrado';
}
